import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  BookOpen,
  MapPin,
  Hash,
  Camera,
  X,
  Building2,
  Tag,
  Barcode,
  Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRANCHES = [
  "Carbon Valley",
  "Centennial Park",
  "Erie",
  "Farr",
  "LINC",
  "Riverside"
];

const MEMBERS = [
  "Eaton",
  "Fort Lupton",
  "Glenn A. Jones",
  "Hudson",
  "Nantes (Gilcrest)",
  "Northern Plains (Ault)",
  "Platteville"
];

const PURPOSES = [
  { value: "book_display", label: "Book Display" },
  { value: "applicable_to_work", label: "Applicable to Work" },
  { value: "readers_advisory", label: "Readers Advisory" },
  { value: "collection_suggestion", label: "Collection Suggestion" },
  { value: "other", label: "Other" }
];

const PRESET_PURPOSE_TAGS = [
  "New Arrival",
  "Staff Pick",
  "Popular",
  "Award Winner",
  "Local Author",
  "Series",
  "Reference",
  "Reserve"
];

const initialFormData = {
  title: "",
  author: "",
  call_number: "",
  barcode: "",
  branch: "",
  member: "",
  purpose: "",
  purpose_tags: [],
  book_display_id: "",
  notes: ""
};

export const Books = ({ user, viewMode, userLookup = {} }) => {
  const [books, setBooks] = useState([]);
  const [bookDisplays, setBookDisplays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [isEditing, setIsEditing] = useState(false);
  const [formTab, setFormTab] = useState("basic");
  const [purposeTagInput, setPurposeTagInput] = useState("");
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };

  useEffect(() => {
    fetchBooks();
    fetchBookDisplays();
  }, []);

  const fetchBooks = async () => {
    try {
      const response = await axios.get(`${API}/books`);
      setBooks(response.data);
    } catch (error) {
      toast.error("Failed to fetch books");
    } finally {
      setLoading(false);
    }
  };

  const fetchBookDisplays = async () => {
    try {
      const response = await axios.get(`${API}/book-displays`);
      setBookDisplays(response.data);
    } catch (error) {
      console.error("Failed to fetch book displays");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        book_display_id: formData.book_display_id || null
      };

      if (isEditing && selectedBook) {
        await axios.put(`${API}/books/${selectedBook.id}`, submitData);
        toast.success("Book updated successfully");
      } else {
        await axios.post(`${API}/books`, submitData);
        toast.success("Book added successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchBooks();
    } catch (error) {
      toast.error(isEditing ? "Failed to update book" : "Failed to add book");
    }
  };

  const handleDelete = async () => {
    if (!selectedBook) return;
    try {
      await axios.delete(`${API}/books/${selectedBook.id}`);
      toast.success("Book deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedBook(null);
      fetchBooks();
    } catch (error) {
      toast.error("Failed to delete book");
    }
  };

  const handleEdit = (book) => {
    setSelectedBook(book);
    setFormData({
      title: book.title,
      author: book.author,
      call_number: book.call_number || "",
      barcode: book.barcode || "",
      branch: book.branch || "",
      member: book.member || "",
      purpose: book.purpose || "",
      purpose_tags: book.purpose_tags || [],
      book_display_id: book.book_display_id || "",
      notes: book.notes || ""
    });
    setIsEditing(true);
    setFormTab("basic");
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedBook(null);
    setIsEditing(false);
    setFormTab("basic");
    setPurposeTagInput("");
  };

  // Helper function to clean Codabar start/stop characters
  const cleanBarcodeValue = (barcode) => {
    if (!barcode) return barcode;
    // Codabar uses A, B, C, D as start/stop characters - remove them
    return barcode.replace(/^[ABCDabcd]/, '').replace(/[ABCDabcd]$/, '');
  };

  // Barcode Scanner Functions - using html5-qrcode for real barcode detection
  // Supported formats for library books: EAN-13 (ISBN), Code 39, Code 128, UPC-A, UPC-E
  const SUPPORTED_BARCODE_FORMATS = [
    Html5QrcodeSupportedFormats.EAN_13,      // ISBN barcodes (13-digit)
    Html5QrcodeSupportedFormats.EAN_8,       // Short EAN codes
    Html5QrcodeSupportedFormats.UPC_A,       // 12-digit UPC (US)
    Html5QrcodeSupportedFormats.UPC_E,       // Compressed UPC
    Html5QrcodeSupportedFormats.CODE_39,     // Library internal barcodes
    Html5QrcodeSupportedFormats.CODE_128,    // Library internal barcodes
    Html5QrcodeSupportedFormats.CODABAR,     // Library barcodes (Codabar/NW-7)
    Html5QrcodeSupportedFormats.ITF,         // Interleaved 2 of 5
  ];

  const startScanner = async () => {
    setScannerOpen(true);
    setScannerReady(false);
    
    // Wait for dialog to render the element
    setTimeout(async () => {
      try {
        html5QrCodeRef.current = new Html5Qrcode("desktop-barcode-reader", {
          formatsToSupport: SUPPORTED_BARCODE_FORMATS,
          verbose: false
        });
        
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 280, height: 120 },
            aspectRatio: 1.777,
            disableFlip: false
          },
          (decodedText, decodedResult) => {
            // Clean Codabar start/stop characters if present
            const cleanedBarcode = cleanBarcodeValue(decodedText);
            console.log("Scanned:", decodedText, "Cleaned:", cleanedBarcode, "Format:", decodedResult?.result?.format?.formatName);
            setFormData(prev => ({ ...prev, barcode: cleanedBarcode }));
            toast.success(`Barcode scanned: ${cleanedBarcode}`);
            stopScanner();
          },
          (errorMessage) => {
            // Ignore scan errors (normal during scanning)
          }
        );
        setScannerReady(true);
      } catch (err) {
        console.error("Failed to start scanner:", err);
        toast.error("Could not access camera");
        setScannerOpen(false);
      }
    }, 100);
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    setScannerOpen(false);
    setScannerReady(false);
  };

  // Purpose Tags
  const addPurposeTag = () => {
    const trimmedTag = purposeTagInput.trim();
    if (trimmedTag && !formData.purpose_tags.includes(trimmedTag)) {
      setFormData({
        ...formData,
        purpose_tags: [...formData.purpose_tags, trimmedTag]
      });
      setPurposeTagInput("");
    }
  };

  const togglePurposeTag = (tag) => {
    if (formData.purpose_tags.includes(tag)) {
      setFormData({
        ...formData,
        purpose_tags: formData.purpose_tags.filter(t => t !== tag)
      });
    } else {
      setFormData({
        ...formData,
        purpose_tags: [...formData.purpose_tags, tag]
      });
    }
  };

  const removePurposeTag = (tag) => {
    setFormData({
      ...formData,
      purpose_tags: formData.purpose_tags.filter(t => t !== tag)
    });
  };

  const getDisplayName = (displayId) => {
    const display = bookDisplays.find(d => d.id === displayId);
    return display ? display.title : "-";
  };

  const filteredBooks = books.filter((book) => {
    const matchesSearch = book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      book.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.barcode || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (book.call_number || "").toLowerCase().includes(searchQuery.toLowerCase());
    const matchesBranch = branchFilter === "all" || book.branch === branchFilter;
    return matchesSearch && matchesBranch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="books-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="books-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Books
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage your library book inventory
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="add-book-btn"
          className="btn-animate"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Book
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, author, barcode, call number..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-books"
          />
        </div>
        <Select value={branchFilter} onValueChange={setBranchFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="branch-filter">
            <Building2 className="mr-2 h-4 w-4" />
            <SelectValue placeholder="Filter by branch" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Branches</SelectItem>
            {BRANCHES.map((branch) => (
              <SelectItem key={branch} value={branch}>
                {branch}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Books Table */}
      {filteredBooks.length === 0 ? (
        <Card className="border-stone-200" data-testid="no-books">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BookOpen className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">No books found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchQuery || branchFilter !== "all"
                ? "Try adjusting your filters"
                : "Add your first book to the inventory"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-stone-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title / Author</TableHead>
                <TableHead>Call No.</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Member</TableHead>
                <TableHead>Barcode</TableHead>
                <TableHead>Purpose</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBooks.map((book) => (
                <TableRow key={book.id} className="table-row-hover" data-testid={`book-row-${book.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{book.title}</p>
                      <p className="text-sm text-muted-foreground">{book.author}</p>
                      {getStaffName(book.created_by) && (
                        <p className="text-xs text-blue-600 mt-1">
                          Added by: {getStaffName(book.created_by)}
                        </p>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {book.call_number || "-"}
                  </TableCell>
                  <TableCell>
                    {book.branch ? (
                      <Badge variant="outline" className="text-xs">
                        {book.branch}
                      </Badge>
                    ) : "-"}
                  </TableCell>
                  <TableCell>
                    {book.member ? (
                      <Badge variant="secondary" className="text-xs">
                        {book.member}
                      </Badge>
                    ) : "-"}
                  </TableCell>
                  <TableCell className="font-mono text-xs">
                    {book.barcode || "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {book.purpose && (
                        <Badge className="text-xs bg-blue-100 text-blue-700">
                          {PURPOSES.find(p => p.value === book.purpose)?.label || book.purpose}
                        </Badge>
                      )}
                      {book.book_display_id && (
                        <span className="text-xs text-muted-foreground">
                          Display: {getDisplayName(book.book_display_id)}
                        </span>
                      )}
                      {book.purpose_tags && book.purpose_tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {book.purpose_tags.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {book.purpose_tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{book.purpose_tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`book-menu-${book.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(book)}>
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setSelectedBook(book);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Book" : "Add New Book"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the book details" : "Add a new book to your inventory"}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={formTab} onValueChange={setFormTab} className="w-full">
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="location">Location</TabsTrigger>
              <TabsTrigger value="purpose">Purpose</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <TabsContent value="basic" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Book Information</h3>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="title">Title *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          placeholder="Book title"
                          required
                          data-testid="book-title-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="author">Author *</Label>
                        <Input
                          id="author"
                          value={formData.author}
                          onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                          placeholder="Author name"
                          required
                          data-testid="book-author-input"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="call_number">Call Number</Label>
                      <Input
                        id="call_number"
                        value={formData.call_number}
                        onChange={(e) => setFormData({ ...formData, call_number: e.target.value })}
                        placeholder="e.g., 813.54 SMI"
                        data-testid="book-call-number-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Barcode</h3>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        id="barcode"
                        value={formData.barcode}
                        onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                        placeholder="Scan or enter barcode"
                        data-testid="book-barcode-input"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={startScanner}
                      data-testid="scan-barcode-btn"
                    >
                      <Camera className="mr-2 h-4 w-4" /> Scan
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Use camera to scan barcode or type manually
                  </p>
                </div>

              </TabsContent>

              <TabsContent value="location" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Branch</h3>
                  <Select
                    value={formData.branch || "none"}
                    onValueChange={(value) => setFormData({ ...formData, branch: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-branch-select">
                      <SelectValue placeholder="Select branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select Branch --</SelectItem>
                      {BRANCHES.map((branch) => (
                        <SelectItem key={branch} value={branch}>
                          {branch}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-section">
                  <h3 className="font-medium mb-3">Member</h3>
                  <Select
                    value={formData.member || "none"}
                    onValueChange={(value) => setFormData({ ...formData, member: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-member-select">
                      <SelectValue placeholder="Select member" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select Member --</SelectItem>
                      {MEMBERS.map((member) => (
                        <SelectItem key={member} value={member}>
                          {member}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

              </TabsContent>

              <TabsContent value="purpose" className="space-y-4">
                <div className="form-section">
                  <h3 className="font-medium mb-3">Purpose</h3>
                  <Select
                    value={formData.purpose || "none"}
                    onValueChange={(value) => setFormData({ ...formData, purpose: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="book-purpose-select">
                      <SelectValue placeholder="Select purpose" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Select Purpose --</SelectItem>
                      {PURPOSES.map((purpose) => (
                        <SelectItem key={purpose.value} value={purpose.value}>
                          {purpose.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.purpose === "book_display" && (
                  <div className="form-section">
                    <h3 className="font-medium mb-3">Select Book Display</h3>
                    <Select
                      value={formData.book_display_id || "none"}
                      onValueChange={(value) => setFormData({ ...formData, book_display_id: value === "none" ? "" : value })}
                    >
                      <SelectTrigger data-testid="book-display-select">
                        <SelectValue placeholder="Choose a display" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- Select Display --</SelectItem>
                        {bookDisplays.map((display) => (
                          <SelectItem key={display.id} value={display.id}>
                            {display.title} ({display.theme})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="form-section">
                  <h3 className="font-medium mb-3">Purpose Tags</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Add tags to categorize this book's purpose
                  </p>
                  
                  {/* Selected Tags */}
                  {formData.purpose_tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {formData.purpose_tags.map((tag) => (
                        <Badge
                          key={tag}
                          variant="default"
                          className="px-3 py-1"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => removePurposeTag(tag)}
                            className="ml-2 hover:text-red-300"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {/* Custom Tag Input */}
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Add custom tag..."
                      value={purposeTagInput}
                      onChange={(e) => setPurposeTagInput(e.target.value)}
                      onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addPurposeTag())}
                      data-testid="purpose-tag-input"
                    />
                    <Button type="button" variant="outline" onClick={addPurposeTag} data-testid="add-purpose-tag-btn">
                      Add Tag
                    </Button>
                  </div>
                  
                  {/* Preset Tags */}
                  <p className="text-xs text-muted-foreground mb-2">Quick add from presets:</p>
                  <div className="flex flex-wrap gap-2">
                    {PRESET_PURPOSE_TAGS.filter(tag => !formData.purpose_tags.includes(tag)).map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="cursor-pointer hover:bg-muted"
                        onClick={() => togglePurposeTag(tag)}
                        data-testid={`purpose-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        + {tag}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional notes..."
                    rows={2}
                    data-testid="book-notes-input"
                  />
                </div>
              </TabsContent>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-book-btn">
                  {isEditing ? "Update Book" : "Add Book"}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Barcode Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => !open && stopScanner()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="h-5 w-5" />
              Scan Barcode
            </DialogTitle>
            <DialogDescription>
              Point your camera at the barcode - it will be detected automatically
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative bg-black rounded-lg overflow-hidden" style={{ minHeight: "300px" }}>
              <div 
                id="desktop-barcode-reader" 
                ref={scannerRef}
                className="w-full"
              />
              {/* Loading indicator */}
              {!scannerReady && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70">
                  <div className="text-center text-white">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p className="text-sm">Starting camera...</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={stopScanner} className="flex-1">
                Cancel
              </Button>
              <Button 
                variant="secondary"
                onClick={() => {
                  const manual = prompt("Enter barcode manually:");
                  if (manual) {
                    setFormData(prev => ({ ...prev, barcode: manual.trim() }));
                    toast.success("Barcode entered");
                    stopScanner();
                  }
                }} 
                className="flex-1"
              >
                Enter Manually
              </Button>
            </div>
            <p className="text-xs text-center text-muted-foreground">
              The barcode will be detected automatically when visible. If detection fails, use "Enter Manually".
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Book</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedBook?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-book"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
